/**
 * CanvasTabs — Tab bar for the 4-tab clinical canvas.
 * Shows dirty indicators and active tab highlight.
 */

import { cn } from '../../lib/utils'
import type { CanvasTab, CanvasTabState } from '../../hooks/useCanvas'
import { TAB_TITLES } from '../../hooks/useCanvas'

interface CanvasTabsProps {
  activeTab: CanvasTab
  tabs: Record<CanvasTab, CanvasTabState>
  onTabChange: (tab: CanvasTab) => void
}

const TAB_ORDER: CanvasTab[] = ['hpi', 'pe', 'assessment_and_plan', 'notes']

const TAB_SHORT_LABELS: Record<CanvasTab, string> = {
  hpi: 'HPI',
  pe: 'PE',
  assessment_and_plan: 'A&P',
  notes: 'Notes',
}

export function CanvasTabs({ activeTab, tabs, onTabChange }: CanvasTabsProps) {
  return (
    <div className="flex border-b border-border" role="tablist" aria-label="Canvas tabs">
      {TAB_ORDER.map(tab => {
        const isActive = activeTab === tab
        const hasContent = !!tabs[tab].content
        const isDirty = tabs[tab].isDirty

        return (
          <button
            key={tab}
            role="tab"
            aria-selected={isActive}
            aria-label={TAB_TITLES[tab]}
            onClick={() => onTabChange(tab)}
            className={cn(
              'relative px-4 py-2 text-sm font-display font-medium transition-colors',
              'hover:text-foreground hover:bg-muted/50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'text-foreground border-b-2 border-primary bg-background'
                : 'text-muted-foreground',
              hasContent && !isActive && 'text-foreground/70'
            )}
          >
            <span className="flex items-center gap-1.5">
              {TAB_SHORT_LABELS[tab]}
              {hasContent && (
                <span
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
                    'bg-ai-amber-soft text-ai-amber-text',
                    !isDirty && 'opacity-60'
                  )}
                  title={isDirty ? 'AI Draft — unsaved changes' : 'AI Draft — saved'}
                >
                  DRAFT
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
